import { useEffect, useMemo, useState } from "react";
import { fetchHomepageSections, updateHomepageSections } from "../api";
import {
    buildManualSectionBooks,
    HOME_PAGE_SECTION_DEFAULTS,
    HOME_PAGE_SECTION_DEFINITIONS,
    normalizeHomepageSections,
} from "../homepageSections";

export default function AdminHomepageSectionManager({ user, books = [] }) {
    const [sections, setSections] = useState(HOME_PAGE_SECTION_DEFAULTS);
    const [searches, setSearches] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        let isMounted = true;

        const loadSections = async () => {
            setLoading(true);
            setError("");

            try {
                const data = await fetchHomepageSections();
                if (isMounted) {
                    setSections(normalizeHomepageSections(data.sections));
                }
            } catch (err) {
                if (isMounted) {
                    setError(err.message || "Failed to load homepage section settings");
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadSections();

        return () => {
            isMounted = false;
        };
    }, []);

    const booksById = useMemo(() => {
        return new Map(books.map((book) => [String(book?._id ?? book?.id ?? ""), book]));
    }, [books]);

    const updateSection = (sectionKey, updater) => {
        setSections((current) => {
            const currentSection = current[sectionKey] || HOME_PAGE_SECTION_DEFAULTS[sectionKey];
            const nextValue = typeof updater === "function" ? updater(currentSection) : updater;

            return {
                ...current,
                [sectionKey]: {
                    ...currentSection,
                    ...nextValue,
                },
            };
        });
        setMessage("");
    };

    const handleLimitChange = (sectionKey, value) => {
        const parsed = Number(value);
        updateSection(sectionKey, {
            itemLimit: Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1,
        });
    };

    const handleToggleBook = (sectionKey, bookId) => {
        updateSection(sectionKey, (currentSection) => {
            const normalizedBookId = String(bookId);
            const alreadySelected = currentSection.bookIds.includes(normalizedBookId);

            return {
                bookIds: alreadySelected
                    ? currentSection.bookIds.filter((id) => id !== normalizedBookId)
                    : [...currentSection.bookIds, normalizedBookId],
            };
        });
    };

    const handleToggleActive = (sectionKey) => {
        updateSection(sectionKey, (currentSection) => ({
            isActive: !currentSection.isActive
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setError("");
        setMessage("");

        try {
            const data = await updateHomepageSections(user.token, sections);
            setSections(normalizeHomepageSections(data.sections));
            setMessage("Homepage section settings saved.");
        } catch (err) {
            setError(err.message || "Failed to save homepage section settings");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text-light)" }}>
                Loading homepage section settings...
            </div>
        );
    }

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{
                background: "white",
                border: "1px solid var(--border)",
                borderRadius: 16,
                padding: 20,
                display: "flex",
                justifyContent: "space-between",
                gap: 16,
                flexWrap: "wrap",
                alignItems: "center",
            }}>
                <div>
                    <h2 style={{ margin: "0 0 6px 0", fontSize: "1.4rem" }}>Homepage Sections</h2>
                    <p style={{ margin: 0, color: "var(--text-light)" }}>
                        Pick the books shown in each homepage section and set how many items appear there.
                    </p>
                </div>
                <button
                    className="btn btn-forest"
                    onClick={handleSave}
                    disabled={saving}
                    style={{ minWidth: 150, justifyContent: "center", opacity: saving ? 0.7 : 1 }}
                >
                    {saving ? "Saving..." : "Save Changes"}
                </button>
            </div>

            {error && (
                <div style={{
                    background: "#fff4f4",
                    border: "1px solid #f3b5b5",
                    color: "#8b1e1e",
                    borderRadius: 12,
                    padding: "12px 16px",
                }}>
                    {error}
                </div>
            )}

            {message && (
                <div style={{
                    background: "#eef9f0",
                    border: "1px solid #b9dfc0",
                    color: "#235f2f",
                    borderRadius: 12,
                    padding: "12px 16px",
                }}>
                    {message}
                </div>
            )}

            {HOME_PAGE_SECTION_DEFINITIONS.map((definition) => {
                const section = sections[definition.key] || HOME_PAGE_SECTION_DEFAULTS[definition.key];
                const selectedBooks = buildManualSectionBooks(books, section);
                const selectedIdSet = new Set(section.bookIds);
                const searchValue = searches[definition.key] || "";
                const filteredBooks = books.filter((book) => {
                    const haystack = `${book.title || ""} ${book.author || ""}`.toLowerCase();
                    return haystack.includes(searchValue.toLowerCase());
                });

                return (
                    <div
                        key={definition.key}
                        style={{
                            background: "white",
                            border: "1px solid var(--border)",
                            borderRadius: 16,
                            padding: 20,
                            display: "flex",
                            flexDirection: "column",
                            gap: 18,
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <label style={{ display: "flex", alignItems: "center", cursor: "pointer", gap: 8 }}>
                                    <input 
                                        type="checkbox" 
                                        checked={section.isActive} 
                                        onChange={() => handleToggleActive(definition.key)}
                                        style={{ width: 18, height: 18, cursor: "pointer" }}
                                    />
                                    <span style={{ fontSize: "0.9rem", fontWeight: 600, color: section.isActive ? "var(--forest)" : "var(--text-light)" }}>
                                        {section.isActive ? "Enabled" : "Disabled"}
                                    </span>
                                </label>
                                <div>
                                    <h3 style={{ margin: "0 0 4px 0", fontSize: "1.15rem", opacity: section.isActive ? 1 : 0.5 }}>{definition.label}</h3>
                                    <p style={{ margin: 0, color: "var(--text-light)", fontSize: "0.92rem", opacity: section.isActive ? 1 : 0.5 }}>
                                        {definition.description}
                                    </p>
                                </div>
                            </div>

                            {definition.key !== "authors" && (
                                <label style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 120 }}>
                                    <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--text-light)" }}>
                                        Show Count
                                    </span>
                                    <input
                                        type="number"
                                        min="1"
                                        max="24"
                                        value={section.itemLimit}
                                        onChange={(event) => handleLimitChange(definition.key, event.target.value)}
                                        style={{
                                            padding: "10px 12px",
                                            borderRadius: 10,
                                            border: "1px solid var(--border)",
                                            fontFamily: "inherit",
                                        }}
                                    />
                                </label>
                            )}
                        </div>

                        {definition.key !== "authors" && (
                            <>
                                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                    <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--text-light)" }}>
                                        Selected Books ({section.bookIds.length})
                                    </div>

                                    {selectedBooks.length === 0 ? (
                                        <div style={{
                                            padding: "14px 16px",
                                            borderRadius: 12,
                                            background: "#fcf8f8",
                                            color: "var(--text-light)",
                                        }}>
                                            No manual books selected yet. This section will keep its automatic fallback until books are chosen here.
                                        </div>
                                    ) : (
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                                            {section.bookIds.map((bookId, index) => {
                                                const book = booksById.get(String(bookId));
                                                if (!book) return null;

                                                return (
                                                    <button
                                                        key={bookId}
                                                        onClick={() => handleToggleBook(definition.key, bookId)}
                                                        style={{
                                                            border: "1px solid #e8c9c9",
                                                            background: "#fff8f8",
                                                            color: "#8B1A1A",
                                                            borderRadius: 999,
                                                            padding: "8px 14px",
                                                            display: "flex",
                                                            gap: 8,
                                                            alignItems: "center",
                                                            cursor: "pointer",
                                                            fontFamily: "inherit",
                                                        }}
                                                        title="Remove from this section"
                                                    >
                                                        <span style={{ fontWeight: 700 }}>{index + 1}.</span>
                                                        <span>{book.title}</span>
                                                        <span style={{ opacity: 0.6 }}>x</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                    <input
                                        type="text"
                                        placeholder={`Search books for ${definition.label}...`}
                                        value={searchValue}
                                        onChange={(event) => setSearches((current) => ({
                                            ...current,
                                            [definition.key]: event.target.value,
                                        }))}
                                        style={{
                                            padding: "12px 14px",
                                            borderRadius: 10,
                                            border: "1px solid var(--border)",
                                            fontFamily: "inherit",
                                        }}
                                    />

                                    <div style={{
                                        border: "1px solid #efe5d8",
                                        borderRadius: 14,
                                        maxHeight: 320,
                                        overflowY: "auto",
                                    }}>
                                        {filteredBooks.length === 0 ? (
                                            <div style={{ padding: 16, color: "var(--text-light)" }}>
                                                No books match this search.
                                            </div>
                                        ) : (
                                            filteredBooks.map((book) => {
                                                const selected = selectedIdSet.has(String(book._id));

                                                return (
                                                    <label
                                                        key={book._id}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 12,
                                                            padding: "12px 14px",
                                                            borderBottom: "1px solid #f4ece2",
                                                            background: selected ? "#fff7f1" : "white",
                                                            cursor: "pointer",
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selected}
                                                            onChange={() => handleToggleBook(definition.key, book._id)}
                                                        />
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: 700, color: "var(--ink)" }}>{book.title}</div>
                                                            <div style={{ fontSize: "0.85rem", color: "var(--text-light)" }}>
                                                                {book.author || "Unknown author"}
                                                            </div>
                                                        </div>
                                                    </label>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
